import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signOut, fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import Sidebar from './Sidebar/Sidebar';
import DashboardBar from './DashboardBar/DashboardBar';
import UserProfileForm from './UserProfileForm/UserProfileForm';
import HomeView from './HomeView/HomeView';
import MFASetup from './MFASetup';
import { DeleteAccountForm } from './AccountSettings';
import BucketList from './BucketList';
import CreateBucketForm from './CreateBucketForm';
import ObjectList from './ObjectList';
import UploadObjectForm from './UploadObjectForm';
import SubscriptionPage from './SubscriptionPage/SubscriptionPage';
import OtpSharesPage from './OtpSharesPage';
import ActivityModal from './ActivityModal/ActivityModal';
import { Bucket } from '../types';
import { isProfileComplete } from '../utils/profileUtils';
import DownloadPage from './DownloadPage/DownloadPage';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedBucket, setSelectedBucket] = useState<Bucket | null>(null);
  const [allBuckets, setAllBuckets] = useState<Bucket[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [username, setUsername] = useState<string>('');
  const [bucketStats, setBucketStats] = useState<{ count: number; totalSize: number }>({ count: 0, totalSize: 0 });
  const [storageQuota, setStorageQuota] = useState<{ usedGB: number; totalGB: number; percentUsed: number } | null>(null);
  const [currentView, setCurrentView] = useState<string>('home');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
  const [accountAction, setAccountAction] = useState<'none' | 'edit' | 'delete' | 'security' | 'subscription'>('none');
  const [isAdmin, setIsAdmin] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [userProfile, setUserProfile] = useState<{
    firstName: string;
    lastName: string;
    phoneNumber: string;
    title: string;
  }>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    title: ''
  });

  useEffect(() => {
    checkAuth();
    checkProfileStatus();
    fetchUsername();
    checkAdminStatus();
    
    // Check for view query parameter
    const viewParam = searchParams.get('view');
    if (viewParam === 'security' || viewParam === 'subscription') {
      setCurrentView('account');
      setAccountAction(viewParam as 'security' | 'subscription');
    } else if (viewParam === 'profile') {
      setCurrentView('account');
      setAccountAction('edit');
    } else if (viewParam) {
      setCurrentView(viewParam);
    }
  }, [searchParams]);

  const checkAuth = async () => {
    try {
      await fetchAuthSession();
    } catch (error) {
      navigate('/login');
    }
  };

  const checkAdminStatus = async () => {
    try {
      const session = await fetchAuthSession();
      const groups = (session.tokens?.idToken?.payload?.['cognito:groups'] ?? []) as string[];
      setIsAdmin(groups.includes('Administrators'));
    } catch {
      setIsAdmin(false);
    }
  };

  const checkProfileStatus = async () => {
    try {
      const attributes = await fetchUserAttributes();
      
      // Use backward-compatible profile completion check
      // This handles existing users who registered before the profileComplete attribute was added
      if (!isProfileComplete(attributes)) {
        navigate('/complete-profile');
      }
    } catch (error) {
      console.error('Failed to check profile status:', error);
      // Fail-safe: redirect to profile completion if status check fails
      navigate('/complete-profile');
    }
  };

  const fetchUsername = async () => {
    try {
      const attributes = await fetchUserAttributes();
      // Try to get custom username first, fallback to cognito username, then email
      const customUsername = attributes['custom:username'];
      const cognitoUsername = attributes['cognito:username'];
      const email = attributes['email'];
      
      setUsername(customUsername || cognitoUsername || email || '');
      
      // Load user profile data
      setUserProfile({
        firstName: attributes['given_name'] || '',
        lastName: attributes['family_name'] || '',
        phoneNumber: attributes['phone_number'] || '',
        title: attributes['custom:title'] || ''
      });
    } catch (error) {
      console.error('Failed to fetch username:', error);
    }
  };

  const handleBucketStatsUpdate = (count: number, totalSize: number, quota?: { usedGB: number; totalGB: number; percentUsed: number }) => {
    setBucketStats({ count, totalSize });
    if (quota) {
      setStorageQuota(quota);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleBucketCreated = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleBucketDeleted = () => {
    setSelectedBucket(null);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleUploadComplete = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleDeleteAccount = () => {
    setAccountAction('delete');
  };

  const handleNavigate = (viewId: string) => {
    if (viewId === 'security' || viewId === 'subscription') {
      setCurrentView('account');
      setAccountAction(viewId as 'security' | 'subscription');
      navigate('/dashboard?view=account', { replace: true });
      return;
    }
    setCurrentView(viewId);
    navigate(`/dashboard?view=${viewId}`, { replace: true });
    if (viewId !== 'account') {
      setAccountAction('none');
    }
  };

  const handleSidebarCollapseChange = (isCollapsed: boolean) => {
    setIsSidebarCollapsed(isCollapsed);
  };

  const handleProfileClick = () => {
    setCurrentView('account');
    setAccountAction('edit');
    navigate('/dashboard?view=account', { replace: true });
  };



  return (
    <div className="dashboard-layout">
      <Sidebar 
        currentView={currentView} 
        onNavigate={handleNavigate}
        onCollapseChange={handleSidebarCollapseChange}
      />
      
      <div className={`dashboard-main ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <DashboardBar
          username={username}
          isAdmin={isAdmin}
          onProfileClick={handleProfileClick}
          onActivityClick={() => setShowActivityModal(true)}
          onAdminClick={() => navigate('/admin')}
          onLogout={handleLogout}
        />
        {showActivityModal && <ActivityModal onClose={() => setShowActivityModal(false)} />}

        <div className="dashboard-content" style={
          currentView === 'crates' || currentView === 'upload'
            ? { padding: 0, display: 'block', gridTemplateColumns: 'none' }
            : currentView === 'home' || currentView === 'account' || currentView === 'secrets' || currentView === 'report' || currentView === 'resources' || currentView === 'download'
            ? { padding: 0, display: 'block', gridTemplateColumns: 'none', width: '100%', maxWidth: 'none' }
            : {}
        }>
          {currentView === 'home' ? (
            <div style={{ width: '100%', height: '100%' }}>
              <HomeView isAdmin={isAdmin} />
            </div>
          ) : currentView === 'crates' ? (
            <div style={{ display: 'flex', gap: '1.5rem', height: 'calc(100vh - 80px)', width: '100%', padding: '2rem' }}>
              <div style={{ flex: '0 0 350px', overflowY: 'auto', paddingRight: '1rem' }}>
                <BucketList
                  onSelectBucket={setSelectedBucket}
                  selectedBucket={selectedBucket}
                  refreshTrigger={refreshTrigger}
                  onBucketDeleted={handleBucketDeleted}
                  onStatsUpdate={handleBucketStatsUpdate}
                  onBucketsLoaded={setAllBuckets}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', paddingLeft: '1.5rem', borderLeft: '1px solid #e0e0e0' }}>
                {selectedBucket ? (
                  <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)', height: '100%', width: '100%' }}>
                    <ObjectList
                      bucketName={selectedBucket.name}
                      bucket={selectedBucket}
                      refreshTrigger={refreshTrigger}
                      allBuckets={allBuckets}
                    />
                  </div>
                ) : (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: 'white',
                    fontSize: '1.1rem'
                  }}>
                    Select a data crate to view its contents
                  </div>
                )}
              </div>
            </div>
          ) : currentView === 'upload' ? (
            <div style={{ display: 'flex', gap: '1.5rem', height: 'calc(100vh - 80px)', width: '100%', padding: '2rem' }}>
              <div style={{ flex: '0 0 350px', overflowY: 'auto', paddingRight: '1rem' }}>
                <BucketList
                  onSelectBucket={setSelectedBucket}
                  selectedBucket={selectedBucket}
                  refreshTrigger={refreshTrigger}
                  onBucketDeleted={handleBucketDeleted}
                  onStatsUpdate={handleBucketStatsUpdate}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', paddingLeft: '1.5rem', borderLeft: '1px solid #e0e0e0' }}>
                {selectedBucket ? (
                  <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)', height: '100%', width: '100%' }}>
                    <UploadObjectForm
                      selectedBucket={selectedBucket}
                      onUploadComplete={handleUploadComplete}
                      storageQuota={isAdmin ? null : storageQuota}
                    />
                  </div>
                ) : (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%',
                    color: '#999',
                    fontSize: '1.1rem'
                  }}>
                    Select a data crate to upload files
                  </div>
                )}
              </div>
            </div>
          ) : currentView === 'secrets' ? (
            <OtpSharesPage />
          ) : currentView === 'account' ? (
            <div className="account-view" style={{ 
              display: 'flex',
              gap: '2rem',
              padding: '2rem',
              height: 'calc(100vh - 80px)',
              width: '100%'
            }}>
              <div className="account-actions" style={{
                flex: '0 0 250px',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                <h2 style={{ marginBottom: '1rem' }}>Account Settings</h2>
                <button
                  onClick={handleProfileClick}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: accountAction === 'edit' ? '#e6ac00' : '#ffc107',
                    color: '#000',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    textAlign: 'left',
                    width: '100%'
                  }}
                >
                  Edit Profile
                </button>
                <button
                  onClick={handleDeleteAccount}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: accountAction === 'delete' ? '#c82333' : '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    textAlign: 'left',
                    width: '100%'
                  }}
                >
                  Delete Account
                </button>
                <button
                  onClick={() => setAccountAction('security')}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: accountAction === 'security' ? '#e6ac00' : '#ffc107',
                    color: '#000',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    textAlign: 'left',
                    width: '100%'
                  }}
                >
                  Security
                </button>
                {!isAdmin && (
                  <button
                    onClick={() => setAccountAction('subscription')}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: accountAction === 'subscription' ? '#e6ac00' : '#ffc107',
                      color: '#000',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      textAlign: 'left',
                      width: '100%'
                    }}
                  >
                    Subscription Management
                  </button>
                )}
              </div>
              <div className="account-content" style={{
                flex: 1,
                minWidth: 0,
                background: 'white',
                borderRadius: '12px',
                padding: '2rem',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                overflowY: 'auto'
              }}>
                {accountAction === 'edit' ? (
                  <UserProfileForm onCancel={() => setAccountAction('none')} />
                ) : accountAction === 'delete' ? (
                  <DeleteAccountForm
                    username={username}
                    onCancel={() => setAccountAction('none')}
                  />
                ) : accountAction === 'security' ? (
                  <MFASetup />
                ) : accountAction === 'subscription' ? (
                  <SubscriptionPage />
                ) : (
                  <p style={{ color: '#666' }}>
                    Select an action from the left to manage your account settings.
                  </p>
                )}
              </div>
            </div>
          ) : currentView === 'download' ? (
            <DownloadPage />
          ) : currentView === 'report' ? (
            <div style={{ padding: '2rem' }}>
              <h2 style={{ marginBottom: '0.25rem', fontSize: '1.5rem', fontWeight: 700, color: 'white' }}>Get Support</h2>
              <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '2rem', fontSize: '14px' }}>
                Reach out to our team if you encounter any issues or have feedback.
              </p>
              <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', maxWidth: '640px' }}>
                <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Contact Support</h3>
                <p style={{ color: '#555', lineHeight: '1.7', marginBottom: '1rem' }}>
                  If you encounter any problems or have feedback, please contact our support team at{' '}
                  <a href="mailto:support@datacrate.now" style={{ color: '#667eea', textDecoration: 'none', fontWeight: 500 }}>
                    support@datacrate.now
                  </a>.
                </p>
                <p style={{ color: '#555', lineHeight: '1.7' }}>
                  Alternatively, you can report issues directly via our{' '}
                  <a href="https://github.com/anthropics/claude-code/issues" target="_blank" rel="noopener noreferrer" style={{ color: '#667eea', textDecoration: 'none', fontWeight: 500 }}>
                    issue tracker
                  </a>.
                </p>
              </div>
            </div>
          ) : currentView === 'resources' ? (
            <div style={{ padding: '2rem' }}>
              <h2 style={{ marginBottom: '0.25rem', fontSize: '1.5rem', fontWeight: 700, color: 'white' }}>Help &amp; Resources</h2>
              <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '2rem', fontSize: '14px' }}>
                Guides and documentation to help you get the most out of Data Crate.
              </p>
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 280px', background: 'white', borderRadius: '12px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                  <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Getting Started</h3>
                  <ul style={{ lineHeight: '2', paddingLeft: '1.25rem', color: '#555' }}>
                    <li>Create a data crate to organise your files</li>
                    <li>Upload files and folders to your crates</li>
                    <li>Share files with others using secure links</li>
                    <li>Monitor your storage usage in the dashboard</li>
                  </ul>
                </div>
                <div style={{ flex: '1 1 280px', background: 'white', borderRadius: '12px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                  <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Documentation</h3>
                  <ul style={{ lineHeight: '2', paddingLeft: '1.25rem' }}>
                    <li><a href="#" onClick={(e) => e.preventDefault()} style={{ color: '#667eea', textDecoration: 'none' }}>User Guide</a></li>
                    <li><a href="#" onClick={(e) => e.preventDefault()} style={{ color: '#667eea', textDecoration: 'none' }}>API Documentation</a></li>
                    <li><a href="#" onClick={(e) => e.preventDefault()} style={{ color: '#667eea', textDecoration: 'none' }}>Security Best Practices</a></li>
                    <li><a href="#" onClick={(e) => e.preventDefault()} style={{ color: '#667eea', textDecoration: 'none' }}>FAQ</a></li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div>
                <div style={{ marginBottom: '2rem' }}>
                  <CreateBucketForm
                    onBucketCreated={handleBucketCreated}
                    isAdmin={isAdmin}
                  />
                </div>
                <BucketList
                  onSelectBucket={setSelectedBucket}
                  selectedBucket={selectedBucket}
                  refreshTrigger={refreshTrigger}
                  onBucketDeleted={handleBucketDeleted}
                  onStatsUpdate={handleBucketStatsUpdate}
                />
              </div>

              <div>
                {selectedBucket ? (
                  <>
                    <UploadObjectForm
                      selectedBucket={selectedBucket}
                      onUploadComplete={handleUploadComplete}
                      storageQuota={isAdmin ? null : storageQuota}
                    />
                    <div style={{ marginTop: '2rem' }}>
                      <ObjectList
                        bucketName={selectedBucket.name}
                        bucket={selectedBucket}
                        refreshTrigger={refreshTrigger}
                        allBuckets={allBuckets}
                      />
                    </div>
                  </>
                ) : (
                  <div className="object-list-container">
                    <p style={{ textAlign: 'center', color: '#999' }}>
                      Select a data crate to view and manage objects
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        
        {/* Footer */}
        <footer style={{
          padding: '2rem',
          textAlign: 'center',
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f9fafb',
          marginTop: 'auto'
        }}>
          <div style={{
            fontSize: '0.875rem',
            color: '#000',
            marginBottom: '0.5rem',
            fontFamily: 'inherit'
          }}>
            © Copyright 2026,{' '}
            <span style={{ fontFamily: "'Alike', serif", fontWeight: 700, color: '#667eea' }}>Data </span>
            <span style={{ fontFamily: "'Alike', serif", fontWeight: 700, color: '#764ba2' }}>Crate</span>
            {' '}All rights reserved.
          </div>
          <div style={{
            display: 'flex',
            gap: '2rem',
            justifyContent: 'center',
            fontSize: '0.875rem'
          }}>
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); alert('Terms of Service page coming soon!'); }}
              style={{ color: '#000', textDecoration: 'none' }}
            >
              Terms of Service
            </a>
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); alert('Privacy Policy page coming soon!'); }}
              style={{ color: '#000', textDecoration: 'none' }}
            >
              Privacy Policy
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default DashboardPage;
